import type { PlaceCategory } from '../../hooks/useNearbyPlaces';

/**
 * Line icons for nearby-place categories.
 *
 * These replaced the emoji set (🎓 🏥 🛒 🍽️ ...), which rendered differently on
 * every OS and read as generated rather than designed. One drawing style for
 * all eight: 24px grid, 1.7 stroke, round caps - the same geometry as the
 * lucide icons used across the site, whose path data most of these are.
 * WORSHIP is drawn here because lucide has no mosque.
 *
 * The same data drives both the React icon and the HTML string the map's
 * markers need (Google's AdvancedMarkerElement takes a DOM node, not JSX).
 */

type Shape = ['path', { d: string }] | ['rect', { x: number; y: number; width: number; height: number; rx: number }];

const SHAPES: Record<PlaceCategory, Shape[]> = {
  // lucide: graduation-cap
  EDUCATION: [
    ['path', { d: 'M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z' }],
    ['path', { d: 'M22 10v6' }],
    ['path', { d: 'M6 12.5V16a6 3 0 0 0 12 0v-3.5' }],
  ],
  // lucide: hospital
  HEALTHCARE: [
    ['path', { d: 'M12 7v4' }],
    ['path', { d: 'M14 21v-3a2 2 0 0 0-4 0v3' }],
    ['path', { d: 'M14 9h-4' }],
    ['path', { d: 'M18 11h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2' }],
    ['path', { d: 'M18 21V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16' }],
  ],
  // lucide: shopping-basket
  SHOPPING: [
    ['path', { d: 'm15 11-1 9' }],
    ['path', { d: 'm19 11-4-7' }],
    ['path', { d: 'M2 11h20' }],
    ['path', { d: 'm3.5 11 1.6 7.4a2 2 0 0 0 2 1.6h9.8a2 2 0 0 0 2-1.6l1.7-7.4' }],
    ['path', { d: 'M4.5 15.5h15' }],
    ['path', { d: 'm5 11 4-7' }],
    ['path', { d: 'm9 11 1 9' }],
  ],
  // lucide: utensils-crossed
  DINING: [
    ['path', { d: 'm16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8' }],
    ['path', { d: 'M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7' }],
    ['path', { d: 'm2.1 21.8 6.4-6.3' }],
    ['path', { d: 'm19 5-7 7' }],
  ],
  // lucide: trees
  RECREATION: [
    ['path', { d: 'M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z' }],
    ['path', { d: 'M7 16v6' }],
    ['path', { d: 'M13 19v3' }],
    ['path', { d: 'M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5' }],
  ],
  // Drawn to match: a dome with its finial over the prayer hall, an arched
  // door, and a minaret with its balcony.
  WORSHIP: [
    ['path', { d: 'M6 12a5 5 0 0 1 10 0' }],
    ['path', { d: 'M11 7V4.5' }],
    ['path', { d: 'M5 21v-9h12v9' }],
    ['path', { d: 'M9 21v-3a2 2 0 0 1 4 0v3' }],
    ['path', { d: 'M19.5 21V9.5' }],
    ['path', { d: 'M19.5 9.5 21 7l1.5 2.5V21' }],
    ['path', { d: 'M19.5 14h3' }],
    ['path', { d: 'M2 21h21' }],
  ],
  // lucide: landmark
  FINANCE: [
    ['path', { d: 'M10 18v-7' }],
    ['path', { d: 'M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z' }],
    ['path', { d: 'M14 18v-7' }],
    ['path', { d: 'M18 18v-7' }],
    ['path', { d: 'M3 22h18' }],
    ['path', { d: 'M6 18v-7' }],
  ],
  // lucide: bus-front
  TRANSPORTATION: [
    ['path', { d: 'M4 6 2 7' }],
    ['path', { d: 'M10 6h4' }],
    ['path', { d: 'm22 7-2-1' }],
    ['rect', { width: 16, height: 16, x: 4, y: 3, rx: 2 }],
    ['path', { d: 'M4 11h16' }],
    ['path', { d: 'M8 15h.01' }],
    ['path', { d: 'M16 15h.01' }],
    ['path', { d: 'M6 19v2' }],
    ['path', { d: 'M18 21v-2' }],
  ],
};

const STROKE = 1.7;

interface PoiIconProps {
  category: PlaceCategory;
  size?: number;
  className?: string;
}

export function PoiIcon({ category, size = 20, className }: PoiIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {SHAPES[category].map(([tag, attrs], i) =>
        tag === 'path' ? <path key={i} {...attrs} /> : <rect key={i} {...attrs} />
      )}
    </svg>
  );
}

/** The same icon as an SVG string, for DOM-built map markers. */
export function poiIconMarkup(category: PlaceCategory, size: number, color: string): string {
  const body = SHAPES[category]
    .map(([tag, attrs]) =>
      tag === 'path'
        ? `<path d="${attrs.d}"/>`
        : `<rect x="${attrs.x}" y="${attrs.y}" width="${attrs.width}" height="${attrs.height}" rx="${attrs.rx}"/>`
    )
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" ` +
    `fill="none" stroke="${color}" stroke-width="${STROKE}" stroke-linecap="round" stroke-linejoin="round" ` +
    `aria-hidden="true">${body}</svg>`
  );
}
